import React, { useState, useEffect, useCallback } from "react";
import { FaUser, FaEnvelope, FaUserTag, FaIdBadge, FaSync } from "react-icons/fa";
import { authAPI } from "../../services/Api";
import { useAuth } from "../../hooks/useAuth";
import styles from "./Profile.module.css";

const Profile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch profile data
  const fetchProfile = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await authAPI.getProfile();

      if (response.data?.success) {
        setProfileData(response.data.data);
      } else {
        setError(response.data?.message || "Failed to fetch profile");
      }
    } catch (err) {
      console.error("Profile fetch error:", err);
      setError(
        err.response?.data?.message || "Failed to fetch profile. Please try again."
      );
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch profile on mount
  // useEffect(() => {
  //   fetchProfile();
  // }, [fetchProfile]);

  // Handle refresh button click
  const handleRefresh = () => {
    // fetchProfile(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <div className={styles.loader}></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !profileData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>⚠️</div>
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <button className={styles.refreshButton} onClick={handleRefresh}>
            <FaSync />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>My Profile</h1>
        <button
          className={`${styles.refreshButton} ${isRefreshing ? styles.spinning : ""}`}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <FaSync className={isRefreshing ? styles.spinIcon : ""} />
          <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className={styles.profileCard}>
        <div className={styles.cardHeader}>
          <div className={styles.avatarWrapper}>
            <FaUser className={styles.avatarIcon} />
          </div>
          <div className={styles.userInfo}>
            <h2 className={styles.userName}>{user?.name || "User"}</h2>
            <p className={styles.userRole}>
              {profileData?.role?.name || user?.role?.name || "N/A"}
            </p>
          </div>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.infoGrid}>
            {/* User ID */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaIdBadge />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>User ID</span>
                <span className={styles.infoValue}>{user?.id || "N/A"}</span>
              </div>
            </div>

            {/* Name */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaUser />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Full Name</span>
                <span className={styles.infoValue}>{user?.name || "N/A"}</span>
              </div>
            </div>

            {/* Email */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaEnvelope />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Email Address</span>
                <span className={styles.infoValue}>{user?.email || "N/A"}</span>
              </div>
            </div>

            {/* Role */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaUserTag />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Role</span>
                <span className={styles.infoValue}>
                  {profileData?.role?.name || user?.role?.name || "N/A"}
                </span>
              </div>
            </div>

            {/* Role ID */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaIdBadge />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Role ID</span>
                <span className={styles.infoValue}>
                  {profileData?.role?.id || user?.role?.id || "N/A"}
                </span>
              </div>
            </div>

            {/* Role Slug */}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FaUserTag />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Role Slug</span>
                <span className={styles.infoValue}>
                  {profileData?.role?.slug || user?.role?.slug || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.cardFooter}>
          <p className={styles.lastUpdated}>
            Last refreshed: {new Date().toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Profile;
